/**
 * Salaisuus.tsx — kertanäyttöinen salaisuus modaalissa, ei ylälaidan palkissa.
 *
 * Erä 22 A8 (Toni 9.9.2026, toive käytöstä: «ettei tartte rullata ruutua
 * ylös etsimään sitä»). Käyttäjä painoi nappia listan puolivälissä,
 * kutsulinkki tai PIN ilmestyi ruudun ulkopuolelle ja hävisi itsestään —
 * järjestelmä teki jotakin eikä ihminen ollut katsomassa. Sama vikamuoto
 * kuin lomake joka tallentaa change-tapahtumasta ilman vahvistusta.
 *
 * Modaali: pakottaa kuittauksen (sulkeutuu vain «Olen tallentanut» -napista
 * tai Escillä, ei klikkaamalla ohi), tarjoaa kopioinnin, ja sitoo
 * salaisuuden siihen riviin josta se syntyi (otsikossa nimi ja tunnus).
 * Sama komponentti kutsulinkille, kertakäyttöiselle salasanalle,
 * kenttätunnuksen PIN:lle ja PIN-listalle — yksi mekanismi, ei neljää.
 *
 * Kutsulinkille on lisäksi «Lähetä sähköpostilla» (B2), kun vastaanottajan
 * osoite tiedetään ja palvelin osaa lähettää; muuten nappi kertoo miksi ei.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "./kieli";

export interface SalaisuusRivi { selite: string; arvo: string }

export function Salaisuus({
  otsikko, kohde, rivit, vihje, sulje, lahetys, lapset,
}: {
  /** Mitä salaisuus on: «Kutsulinkki», «Kertakäyttöinen salasana», «Kenttätunnus ja PIN». */
  otsikko: string;
  /** Kenen: rivi josta salaisuus syntyi. */
  kohde: string;
  rivit: SalaisuusRivi[];
  vihje?: ReactNode;
  sulje: () => void;
  /** B2: sähköpostilähetys, jos tarjolla. `syy` kertoo miksi ei ole. */
  /** `laheta` kertoo lähtikö (ok) — lause modaalin yläreunassa vaihtuu sen mukaan:
   *  «Näytetään vain nyt» ei pidä enää paikkaansa, jos linkki on saajan postilaatikossa (Toni 11.9.). */
  lahetys?: { osoite: string | null; laheta: () => Promise<{ ok: boolean; viesti: string }>; syy?: string | null };
  lapset?: ReactNode;
}) {
  const [kopioitu, setKopioitu] = useState<number | null>(null);
  const [lahetetty, setLahetetty] = useState<{ ok: boolean; viesti: string } | null>(null);
  const [lahettaa, setLahettaa] = useState(false);
  const nappi = useRef<HTMLButtonElement>(null);
  const dialogi = useRef<HTMLDialogElement>(null);

  // <dialog> + showModal(): tausta himmenee (.ikkuna::backdrop), fokus pysyy ikkunassa.
  // Klikkaus taustaan EI sulje, ja Esc EI sulje ilman kysymystä (Toni 10.9.): Esc on
  // refleksi silloin kun ihminen ei tiedä mitä tehdä — täsmälleen se hetki jolloin
  // salaisuus on vielä kopioimatta. Kertanäyttö jonka voi ohittaa vahingossa on sama
  // kuin ylälaidan palkki, vain hitaammin. Jos arvo on kopioitu, Esc sulkee suoraan.
  useEffect(() => {
    const d = dialogi.current;
    if (d && !d.open) d.showModal();
    nappi.current?.focus();
    const peruutus = (e: Event) => {
      e.preventDefault();
      if (kopioitu !== null || window.confirm(t("sal.sulje_kopioimatta"))) sulje();
    };
    d?.addEventListener("cancel", peruutus);
    return () => { d?.removeEventListener("cancel", peruutus); if (d?.open) d.close(); };
  }, [sulje, kopioitu]);

  const kopioi = (i: number, arvo: string) => {
    void navigator.clipboard?.writeText(arvo).then(() => setKopioitu(i));
  };
  const kaikki = rivit.map((r) => `${r.selite}: ${r.arvo}`).join("\n");

  return (
    <dialog ref={dialogi} className="ikkuna salaisuus" aria-labelledby="salaisuus-otsikko" data-salaisuus={otsikko}>
      <div className="ikkuna__sisalto">
        <div className="ikkuna__yla">
          <strong id="salaisuus-otsikko">{otsikko} · {kohde}</strong>
        </div>
        {/* Uusi ominaisuus ei saa jättää vanhaa lupausta ruudulle: lähetyksen jälkeen arvo
            on saajan postilaatikossa, ja lause sanoo sen. */}
        <p className="dim" data-salaisuus-lause={lahetetty?.ok ? "lahetetty" : "vain-nyt"}>
          {lahetetty?.ok
            ? <>{t("sal.lahetettiin_alku")} <strong>{lahetys?.osoite}</strong>{t("sal.lahetettiin_loppu")}</>
            : t("sal.vain_nyt")}
        </p>
        <table className="tbl salaisuus__taulu">
          <tbody>
            {rivit.map((r, i) => (
              <tr key={i}>
                <td className="dim">{r.selite}</td>
                <td><code className="salasana" style={{ userSelect: "all" }}>{r.arvo}</code></td>
                <td>
                  <button type="button" className="nappi nappi--kevyt" onClick={() => kopioi(i, r.arvo)}>
                    {kopioitu === i ? t("sal.kopioitu") : t("sal.kopioi")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rivit.length > 1 && (
          <p><button type="button" className="nappi nappi--kevyt" onClick={() => kopioi(-1, kaikki)}>
            {kopioitu === -1 ? t("sal.kaikki_kopioitu") : t("sal.kopioi_kaikki")}
          </button>{" "}
          <button type="button" className="nappi nappi--kevyt" onClick={() => window.print()}>{t("sal.tulosta")}</button></p>
        )}
        {vihje && <p className="ikkuna__vihje">{vihje}</p>}
        {lahetys && (
          <p className="salaisuus__lahetys">
            {lahetys.osoite && !lahetys.syy
              ? <button type="button" className="nappi nappi--kevyt" disabled={lahettaa || !!lahetetty}
                        onClick={() => { setLahettaa(true); lahetys.laheta().then(setLahetetty).catch((e: unknown) => setLahetetty({ ok: false, viesti: e instanceof Error ? e.message : t("sal.lahetys_virhe") })).finally(() => setLahettaa(false)); }}>
                  {lahettaa ? t("sal.lahetetaan") : t("sal.laheta_sahkopostilla", { osoite: lahetys.osoite })}
                </button>
              : <span className="dim">{t("sal.ei_lahetysta", { syy: lahetys.syy ?? t("sal.ei_osoitetta") })}</span>}
            {lahetetty && <span role="status"> {lahetetty.viesti}</span>}
          </p>
        )}
        {lapset}
        <div className="ikkuna__napit" style={{ marginTop: 14 }}>
          <button ref={nappi} type="button" className="nappi nappi--paa" onClick={sulje}>{t("sal.tallennettu_sulje")}</button>
        </div>
      </div>
    </dialog>
  );
}
